import { useEffect } from "react"

import { useAuthSessionRetrieve } from "@boundless/bl-api-client/auth/services"
import { toast } from "sonner"

import { getAuthClient } from "@/lib/clients"
import { Client } from "@/lib/constants"

const SESSION_EXPIRED_KEY = "sessionExpired"
const MANUAL_LOGOUT_KEY = "manualLogout"
const IDLE_EXPIRES_AT_KEY = "sessionIdleExpiresAt"

// Poll a second past the BE-reported expiry so we land just after the idle
// window closes, accounting for small clock skew.
const POLL_BUFFER_SECONDS = 1

// Fallback used if the heartbeat ever fails for non-auth reasons (network
// blip, etc.) so the watcher keeps probing periodically instead of stalling.
const FALLBACK_POLL_SECONDS = 60

/**
 * @summary Persist the BE's idle-timeout countdown observed on a response.
 *
 * @desc Called from the axios response interceptor in `lib/clients` whenever
 * an authenticated response carries the `X-Idle-Seconds-Remaining` header.
 * The value is translated to an absolute "expires at" timestamp so the
 * session watcher can schedule its next probe at the moment of expected
 * expiry — and since `localStorage` is shared across same-origin tabs,
 * activity in any one tab keeps the others' watchers from firing prematurely.
 */
export function recordIdleSecondsRemaining(remainingSeconds: number): void {
  const expiresAt = Date.now() + remainingSeconds * 1000
  localStorage.setItem(IDLE_EXPIRES_AT_KEY, String(expiresAt))
}

function getSessionIdleExpiresAt(): number | null {
  const raw = localStorage.getItem(IDLE_EXPIRES_AT_KEY)
  if (raw === null) {
    return null
  }
  const value = Number(raw)
  return Number.isFinite(value) ? value : null
}

function clearSessionIdleExpiresAt(): void {
  localStorage.removeItem(IDLE_EXPIRES_AT_KEY)
}

/**
 * @summary Detect server-side idle session expiry via a self-pacing watcher.
 *
 * @desc The backend's `SessionInactivityMiddleware` invalidates the user's
 * session after `SESSION_IDLE_TIMEOUT` of inactivity, and advertises the
 * countdown on every authenticated response via the `X-Idle-Seconds-Remaining`
 * header (captured by an axios response interceptor in `lib/clients`). This
 * hook reads that observation to schedule its next check at the moment of
 * expected expiry — so while the user is active, regular API traffic keeps
 * the schedule fresh and no extra heartbeats are needed. When the timer
 * fires (or no observation is yet available), the `auth/session` heartbeat
 * is hit; on 401/403 the toast flag is written and `onLogout` runs the
 * mutation-based logout.
 *
 * Mounted once from the `_dashboard` route so it runs for any authenticated
 * area of the app.
 */
export function useSessionWatcher(
  onLogout: (data: { client: Client }) => void,
) {
  const client = getAuthClient()
  const { refetch } = useAuthSessionRetrieve({
    query: {
      enabled: false,
      retry: false,
      refetchOnWindowFocus: false,
    },
  })

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let cancelled = false

    const scheduleFromObservation = (): boolean => {
      const expiresAt = getSessionIdleExpiresAt()
      if (expiresAt === null) {
        return false
      }
      const remainingMs = expiresAt - Date.now()
      if (remainingMs <= 0) {
        return false
      }
      timeoutId = setTimeout(
        () => void checkSession(),
        remainingMs + POLL_BUFFER_SECONDS * 1000,
      )
      return true
    }

    const checkSession = async () => {
      if (cancelled) {
        return
      }

      // Recent activity already told us when the session expires; no need to
      // bother the server.
      if (scheduleFromObservation()) {
        return
      }

      const result = await refetch()
      if (cancelled) {
        return
      }

      if (result.error?.status === 401 || result.error?.status === 403) {
        localStorage.setItem(SESSION_EXPIRED_KEY, "true")
        clearSessionIdleExpiresAt()
        onLogout({ client })
        return
      }

      // The heartbeat response itself populated the cache via the axios
      // interceptor; reschedule from it.
      if (!scheduleFromObservation()) {
        timeoutId = setTimeout(
          () => void checkSession(),
          FALLBACK_POLL_SECONDS * 1000,
        )
      }
    }

    void checkSession()
    return () => {
      cancelled = true
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
      }
    }
  }, [refetch, onLogout, client])
}

export function useSessionToasts(): void {
  useEffect(() => {
    if (localStorage.getItem(MANUAL_LOGOUT_KEY) === "true") {
      localStorage.removeItem(MANUAL_LOGOUT_KEY)
      return
    }
    if (localStorage.getItem(SESSION_EXPIRED_KEY) === "true") {
      toast.info("Your session has expired. Please sign in again.", {
        duration: Infinity,
        closeButton: true,
      })
      localStorage.removeItem(SESSION_EXPIRED_KEY)
    }
  }, [])
}

export function markManualLogout(): void {
  localStorage.setItem(MANUAL_LOGOUT_KEY, "true")
  clearSessionIdleExpiresAt()
}
